/**
 * What this application does after a failed request, owned here rather than inherited.
 *
 * `prd/ARCHITECTURE.md` section 9.1: TanStack Query is Nexus's choice for server state and the
 * design system carries no data layer, so the retry decision has no upstream owner. The library's
 * own default - three attempts for every rejection - is the thing being replaced.
 */
/**
 * Attempts after the first. Two, not three: the failures a retry can actually fix here are a
 * transient 5xx and a dropped connection, both of which clear inside one or two attempts or are
 * not going to. A third attempt mostly lengthens the time a user spends looking at a spinner.
 */
export const RETRY_LIMIT = 2;
/**
 * The 4xx codes that describe a condition which can clear without the request changing: 408 is the
 * server saying it stopped waiting, 429 is the server saying to come back later (RFC 9110
 * sections 15.5.9 and 15.5.29). Every other 4xx says the request itself was wrong.
 */
const RETRYABLE_CLIENT_STATUSES = new Set([408, 429]);
/**
 * The HTTP status behind a rejection, when there is one.
 *
 * Two shapes are read because two are produced: a client that throws an error carrying `status`,
 * and one that throws a wrapper holding the `response`. Reading only the first would quietly
 * classify every client error as "no status", which this policy treats as retryable - so the
 * missing branch would restore the default it exists to replace without failing anything.
 *
 * Anything else - a `TypeError` from a dropped connection, an abort - has no status, and that is
 * the transport-failure case a retry is for.
 */
const statusOf = (error) => {
    if (typeof error !== "object" || error === null) {
        return undefined;
    }
    if ("status" in error && typeof error.status === "number") {
        return error.status;
    }
    if ("response" in error &&
        typeof error.response === "object" &&
        error.response !== null &&
        "status" in error.response &&
        typeof error.response.status === "number") {
        return error.response.status;
    }
    return undefined;
};
/**
 * TanStack Query's `retry` predicate: `failureCount` is the number of attempts already made.
 */
export const shouldRetryQuery = (failureCount, error) => {
    if (failureCount >= RETRY_LIMIT) {
        return false;
    }
    const status = statusOf(error);
    if (status === undefined) {
        return true;
    }
    if (status >= 400 && status < 500) {
        return RETRYABLE_CLIENT_STATUSES.has(status);
    }
    return true;
};
//# sourceMappingURL=retry-policy.js.map